import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import { Text, Flex } from '@chakra-ui/react';
import ChartCard from './ChartCard';
import { analyticsService, LeaderboardEntry } from '../../services/analyticsService';
import { CHART_COLORS } from '../../utils/chartColors';

interface SopByRepChartProps {
  businessIdentifier?: string;
  timeRange?: string;
}

const FONT_FAMILY = 'TT Interphases Pro, Inter, sans-serif';

const getScoreColor = (score: number): string => {
  if (score >= 80) return CHART_COLORS.positive;
  if (score >= 60) return CHART_COLORS.orange;
  return CHART_COLORS.negative;
};

const SopByRepChart: React.FC<SopByRepChartProps> = ({
  businessIdentifier,
  timeRange = '7d',
}) => {
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await analyticsService.getSalesLeaderboard(
          businessIdentifier,
          timeRange,
          'avg_sop',
          20
        );
        setEntries(response.leaderboard || []);
      } catch (err: any) {
        console.error('[SopByRepChart] Error fetching data:', err);
        setError(err.message || 'Failed to load SOP data');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [businessIdentifier, timeRange]);

  const getChartOption = () => {
    if (entries.length === 0) return {};

    const names = entries.map((e) => e.salesperson_name);
    const scores = entries.map((e) => e.avg_sop_score || 0);

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        textStyle: { fontFamily: FONT_FAMILY },
        formatter: (params: any) => {
          const idx = params[0]?.dataIndex;
          const entry = entries[idx];
          if (!entry) return '';
          return `<b>${entry.salesperson_name}</b><br/>SOP Score: ${(entry.avg_sop_score || 0).toFixed(1)}`;
        },
      },
      grid: {
        left: '3%',
        right: '8%',
        bottom: '3%',
        top: '5%',
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        min: 0,
        max: 100,
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#999',
        },
        splitLine: { lineStyle: { color: '#EDF2F7' } },
      },
      yAxis: {
        type: 'category',
        data: names,
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#666',
          width: 80,
          overflow: 'truncate',
        },
        axisLine: { lineStyle: { color: '#E2E8F0' } },
      },
      series: [
        {
          type: 'bar',
          data: scores.map((score) => ({
            value: score,
            itemStyle: {
              color: getScoreColor(score),
              borderRadius: [0, 4, 4, 0],
            },
          })),
          barWidth: '50%',
          label: {
            show: true,
            position: 'right',
            fontFamily: FONT_FAMILY,
            fontSize: 11,
            formatter: (params: any) => params.value?.toFixed(1),
          },
        },
      ],
    };
  };

  return (
    <ChartCard
      title="SOP Adherence by Rep"
      subtitle="Average SOP compliance score per salesperson"
      isLoading={isLoading}
      error={error}
    >
      {entries.length === 0 && !isLoading ? (
        <Flex justify="center" align="center" minH="200px">
          <Text color="gray.400" fontSize="sm">No data available</Text>
        </Flex>
      ) : (
        <ReactECharts
          option={getChartOption()}
          style={{ height: Math.max(300, entries.length * 30) + 'px', width: '100%' }}
          opts={{ renderer: 'canvas' }}
        />
      )}
    </ChartCard>
  );
};

export default SopByRepChart;
